import { translateLabel } from "@/i18n/messages";
import React, { useState } from "react";
import { ArrowLeftIcon, MapPinPlusIcon, PathIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell, PageIntro, Surface } from "@/components/layout/AppShell";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DESTINATION_PROPOSAL_TYPES,
  emptyDestinationProposal,
  normalizeDestinationProposal,
  submitDestinationProposal,
  validateDestinationProposal,
} from "@/lib/destinationProposal";
import {
  TRAIL_PROPOSAL_DIFFICULTIES,
  TRAIL_PROPOSAL_OTHER,
  TRAIL_PROPOSAL_RIDING_STYLES,
  TRAIL_PROPOSAL_SURFACES,
  TRAIL_PROPOSAL_TYPES,
  emptyTrailProposal,
} from "@/lib/trailProposal";

const fieldClass = "mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none";
const selectClass = "mt-2 w-full rounded-xl border border-white/10 bg-[#1c1c1f] px-3 py-2.5 text-white focus:border-[#CCFF00] focus:outline-none";

function GuidedSelect({ label, value, otherValue, options, onChange, onOtherChange }) {
  const selected = options.find((option) => option.value === value);
  return (
    <label className="block">
      <span className="label-tiny">{label} · optional</span>
      <select className={selectClass} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>)}
      </select>
      {selected?.help && <p className="mt-1.5 text-[11px] leading-snug text-zinc-500">{selected.help}</p>}
      {value === TRAIL_PROPOSAL_OTHER && (
        <input
          required
          className={fieldClass}
          value={otherValue}
          onChange={(event) => onOtherChange(event.target.value)}
          placeholder={translateLabel(translateLabel("Suggest a new value"))}
        />
      )}
    </label>
  );
}

function TrailEditor({ open, onOpenChange, initialTrail, onSave }) {
  const [trail, setTrail] = useState(() => initialTrail || emptyTrailProposal());

  React.useEffect(() => {
    if (open) setTrail(initialTrail || emptyTrailProposal());
  }, [open, initialTrail]);

  const update = (key, value) => setTrail((current) => ({ ...current, [key]: value }));

  const save = (event) => {
    event.preventDefault();
    if (!trail.name.trim()) return toast.error(translateLabel("Trail name is required"));
    for (const [label, value] of [
      ["Length", trail.lengthM],
      ["Elevation gain", trail.elevationGainM],
      ["Elevation loss", trail.elevationLossM],
    ]) {
      if (value !== "" && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
        return toast.error(`${label} must be a positive number`);
      }
    }
    if (trail.trailType === TRAIL_PROPOSAL_OTHER && !trail.trailTypeOther.trim()) return toast.error(translateLabel("Enter the proposed trail type"));
    if (trail.ridingStyle === TRAIL_PROPOSAL_OTHER && !trail.ridingStyleOther.trim()) return toast.error(translateLabel("Enter the proposed riding style"));
    if (trail.surface === TRAIL_PROPOSAL_OTHER && !trail.surfaceOther.trim()) return toast.error(translateLabel("Enter the proposed surface"));
    onSave(trail);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1400] max-h-[90dvh] max-w-2xl overflow-y-auto border-white/10 bg-[#121214] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold uppercase">
            {initialTrail ? translateLabel("Edit trail") : translateLabel("Add trail")}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {translateLabel("Add the trail information you know. Leave unknown values empty instead of guessing.")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="mt-2 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel(translateLabel("Trail name · required"))}</span>
              <input required className={fieldClass} value={trail.name} onChange={(e) => update("name", e.target.value)} placeholder={translateLabel(translateLabel("e.g. Flowline"))} />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Difficulty")}</span>
              <select className={selectClass} value={trail.difficulty} onChange={(e) => update("difficulty", e.target.value)}>
                {TRAIL_PROPOSAL_DIFFICULTIES.map((option) => <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>)}
              </select>
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label>
              <span className="label-tiny">{translateLabel(translateLabel("Length (m) · optional"))}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={trail.lengthM} onChange={(e) => update("lengthM", e.target.value)} placeholder="e.g. 850" />
            </label>
            <label>
              <span className="label-tiny">{translateLabel(translateLabel("Elevation gain (m)"))}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={trail.elevationGainM} onChange={(e) => update("elevationGainM", e.target.value)} placeholder="e.g. 25" />
            </label>
            <label>
              <span className="label-tiny">{translateLabel(translateLabel("Elevation loss (m)"))}</span>
              <input type="number" min="0" step="1" className={fieldClass} value={trail.elevationLossM} onChange={(e) => update("elevationLossM", e.target.value)} placeholder="e.g. 180" />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <GuidedSelect
              label={translateLabel("Trail type")}
              value={trail.trailType}
              otherValue={trail.trailTypeOther}
              options={TRAIL_PROPOSAL_TYPES}
              onChange={(value) => update("trailType", value)}
              onOtherChange={(value) => update("trailTypeOther", value)}
            />
            <GuidedSelect
              label={translateLabel("Riding style")}
              value={trail.ridingStyle}
              otherValue={trail.ridingStyleOther}
              options={TRAIL_PROPOSAL_RIDING_STYLES}
              onChange={(value) => update("ridingStyle", value)}
              onOtherChange={(value) => update("ridingStyleOther", value)}
            />
            <GuidedSelect
              label={translateLabel("Surface")}
              value={trail.surface}
              otherValue={trail.surfaceOther}
              options={TRAIL_PROPOSAL_SURFACES}
              onChange={(value) => update("surface", value)}
              onOtherChange={(value) => update("surfaceOther", value)}
            />
          </div>

          <label className="block">
            <span className="label-tiny">{translateLabel(translateLabel("Description · optional"))}</span>
            <textarea
              rows={3}
              className={fieldClass}
              value={trail.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder={translateLabel("Describe the trail character, features and anything riders should know.")}
            />
          </label>

          <button type="submit" className="rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black">
            {initialTrail ? translateLabel("Save trail") : translateLabel("Add trail")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AddDestination() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyDestinationProposal());
  const [saving, setSaving] = useState(false);
  const [trailEditorOpen, setTrailEditorOpen] = useState(false);
  const [editingTrailIndex, setEditingTrailIndex] = useState(null);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const openNewTrail = () => {
    setEditingTrailIndex(null);
    setTrailEditorOpen(true);
  };

  const openTrail = (index) => {
    setEditingTrailIndex(index);
    setTrailEditorOpen(true);
  };

  const saveTrail = (trail) => {
    setForm((current) => {
      const trails = [...current.trails];
      if (editingTrailIndex == null) trails.push(trail);
      else trails[editingTrailIndex] = trail;
      return { ...current, trails };
    });
  };

  const removeTrail = (index) => {
    setForm((current) => ({ ...current, trails: current.trails.filter((_, i) => i !== index) }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const input = normalizeDestinationProposal(form);
    const validation = validateDestinationProposal(input);
    if (validation) return toast.error(validation);
    setSaving(true);
    try {
      await submitDestinationProposal(input);
      toast.success(translateLabel("Destination proposal submitted"), { description: translateLabel("It will be reviewed before anything is published.") });
      setForm(emptyDestinationProposal());
    } catch (error) {
      toast.error(error.message || "Could not submit destination proposal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell size="form" contentClassName="md:pt-28">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-5 inline-flex items-center gap-1.5 text-sm font-bold text-zinc-300 hover:text-white"
      >
        <ArrowLeftIcon size={16} /> Back
      </button>

      <Surface className="md:p-8" data-testid="add-destination-page">
        <PageIntro
          eyebrow="Community contribution"
          icon={MapPinPlusIcon}
          title={translateLabel(translateLabel("Add a missing destination"))}
          description={translateLabel("Know a bike park, trail center or riding area we are missing? Add the destination and any trails you already know. Everything is reviewed before publication.")}
          className="mb-0"
        />

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="add-destination-form">
          <section className="space-y-5">
            <div>
              <h2 className="font-display text-xl font-bold uppercase text-white">{translateLabel("Destination")}</h2>
              <p className="mt-1 text-sm text-zinc-500">{translateLabel(translateLabel("Basic identity and planning information."))}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Name *"))}</span>
                <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={translateLabel(translateLabel("e.g. Bikepark Wissen"))} data-testid="destination-proposal-name" className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Location *"))}</span>
                <input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder={translateLabel(translateLabel("City, region, country"))} data-testid="destination-proposal-location" className={fieldClass} />
              </label>
            </div>

            <label className="block">
              <span className="label-tiny">{translateLabel(translateLabel("Type *"))}</span>
              <select value={form.primaryType} onChange={(e) => update("primaryType", e.target.value)} data-testid="destination-proposal-type" className={selectClass}>
                {DESTINATION_PROPOSAL_TYPES.map((option) => <option key={option.value} value={option.value}>{translateLabel(option.label)}</option>)}
              </select>
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Website · optional"))}</span>
                <input type="url" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://official-site.example" className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Access context · optional"))}</span>
                <input value={form.accessContext} onChange={(e) => update("accessContext", e.target.value)} placeholder={translateLabel(translateLabel("Lift, shuttle, pedal access, ticket or membership notes…"))} className={fieldClass} />
              </label>
            </div>

            <label className="block">
              <span className="label-tiny">{translateLabel(translateLabel("Summary · optional"))}</span>
              <textarea rows={2} value={form.summary} onChange={(e) => update("summary", e.target.value)} placeholder={translateLabel(translateLabel("Short overview shown in destination cards and search results."))} className={fieldClass} />
            </label>

            <label className="block">
              <span className="label-tiny">{translateLabel(translateLabel("Description · optional"))}</span>
              <textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder={translateLabel(translateLabel("Describe the destination, terrain and what riders should know."))} className={fieldClass} />
            </label>

            <div className="grid gap-4 md:grid-cols-3">
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Opening hours · optional"))}</span>
                <textarea rows={3} value={form.openingHours} onChange={(e) => update("openingHours", e.target.value)} placeholder={translateLabel(translateLabel("e.g. Sat–Sun 10:00–17:00"))} className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Season · optional"))}</span>
                <textarea rows={3} value={form.season} onChange={(e) => update("season", e.target.value)} placeholder={translateLabel(translateLabel("e.g. April–October, weather dependent"))} className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Parking · optional"))}</span>
                <textarea rows={3} value={form.parking} onChange={(e) => update("parking", e.target.value)} placeholder={translateLabel(translateLabel("Parking location, restrictions or useful arrival notes."))} className={fieldClass} />
              </label>
            </div>
          </section>

          <section className="border-t border-white/10 pt-7">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <PathIcon size={18} className="text-[#CCFF00]" />
                  <h2 className="font-display text-xl font-bold uppercase text-white">{translateLabel("Trails")}</h2>
                </div>
                <p className="mt-1 text-sm text-zinc-500">
                  Add known trails one by one. You can leave fields you do not know empty.
                </p>
              </div>
              <button
                type="button"
                onClick={openNewTrail}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#CCFF00]/25 bg-[#CCFF00]/[0.06] px-4 py-2 text-xs font-bold text-[#CCFF00] hover:border-[#CCFF00]/45 hover:bg-[#CCFF00]/[0.1]"
                data-testid="destination-add-trail"
              >
                <PlusIcon size={14} weight="bold" /> {translateLabel(translateLabel("Add trail"))}
              </button>
            </div>

            {form.trails.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-white/10 bg-black/10 p-5 text-sm text-zinc-500">
                No trails added yet. This is allowed, but adding known trails makes the proposal much more useful.
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {form.trails.map((trail, index) => (
                  <div key={`${trail.name}-${index}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                    <PathIcon size={16} className="shrink-0 text-[#CCFF00]" />
                    <button type="button" onClick={() => openTrail(index)} className="min-w-0 flex-1 text-left">
                      <div className="truncate text-sm font-bold text-white">{trail.name}</div>
                      <div className="mt-0.5 text-xs text-zinc-500">
                        {[trail.difficulty !== "UNKNOWN" ? trail.difficulty : null, trail.trailType || null, trail.lengthM ? `${trail.lengthM} m` : null].filter(Boolean).join(" · ") || "Details not specified"}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTrail(index)}
                      aria-label={`Remove ${trail.name}`}
                      className="rounded-lg p-2 text-zinc-500 hover:bg-white/5 hover:text-white"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-5 border-t border-white/10 pt-7">
            <div>
              <h2 className="font-display text-xl font-bold uppercase text-white">{translateLabel(translateLabel("Source & contact"))}</h2>
              <p className="mt-1 text-sm text-zinc-500">{translateLabel(translateLabel("Help moderation verify the destination and its trails."))}</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Source URL · optional"))}</span>
                <input type="url" value={form.sourceUrl} onChange={(e) => update("sourceUrl", e.target.value)} placeholder="https://official-source.example" className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Source note · optional"))}</span>
                <input value={form.sourceNote} onChange={(e) => update("sourceNote", e.target.value)} placeholder={translateLabel(translateLabel("Official trail map, operator page, ridden today…"))} className={fieldClass} />
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Your name · optional"))}</span>
                <input value={form.contactName} onChange={(e) => update("contactName", e.target.value)} className={fieldClass} />
              </label>
              <label>
                <span className="label-tiny">{translateLabel(translateLabel("Email · optional"))}</span>
                <input type="email" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} className={fieldClass} />
              </label>
            </div>
          </section>

          <p className="text-xs text-zinc-500">
            Submitting this form never publishes a destination or trail automatically. An admin reviews the complete proposal first.
          </p>
          <button type="submit" disabled={saving} data-testid="destination-proposal-submit" className="rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black disabled:opacity-50">
            {saving ? "Submitting…" : `Submit destination${form.trails.length ? ` + ${form.trails.length} trail${form.trails.length === 1 ? "" : "s"}` : ""}`}
          </button>
        </form>
      </Surface>

      <TrailEditor
        open={trailEditorOpen}
        onOpenChange={setTrailEditorOpen}
        initialTrail={editingTrailIndex == null ? null : form.trails[editingTrailIndex]}
        onSave={saveTrail}
      />
    </AppShell>
  );
}
